import { lazy, Suspense } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useOrgPaths } from "@/hooks/useOrgPaths";
import { useTaskRankingBundle } from "@/hooks/useTaskRankingBundle";
import TransitionOverlay from "@/components/TransitionOverlay";

const ImageRankingCodex = lazy(() => import("@/pages/ImageRankingCodex"));

/**
 * Per-task AI-trait viewer (W3). Resolves the latest "ready" org-scoped ranking-bundle pointer doc
 * for the task and renders the ImageRankingCodex viewer against the org-scoped Storage bundle.
 * Reachable by any active org member (the W5 "View AI Traits" button will link here).
 */
export default function TaskAiTraits() {
  const { trialId, experimentId, taskId } = useParams<{
    trialId: string;
    experimentId: string;
    taskId: string;
  }>();
  const { selectedOrganization, userRole, isSuperAdmin, user } = useAuth();
  const { bundle, loading, error } = useTaskRankingBundle(trialId, experimentId, taskId);
  const paths = useOrgPaths();

  // Per roadmap §2: all active members view; editor/admin/super-admin correct + save.
  const canEdit = isSuperAdmin || userRole === "editor" || userRole === "admin";
  const backToTask = paths.task(trialId, experimentId, taskId);

  if (loading) {
    return <TransitionOverlay open title="Loading AI traits…" subtitle="Fetching this task's ranking bundle…" />;
  }

  if (error || !selectedOrganization) {
    return (
      <AiTraitsNotice
        backTo={backToTask}
        title="Couldn't load AI traits"
        message={error ? error.message : "No organization is selected."}
      />
    );
  }

  if (!bundle) {
    return (
      <AiTraitsNotice
        backTo={backToTask}
        title="No AI traits yet"
        message="This task doesn't have any AI-trait results to review yet."
      />
    );
  }

  return (
    <Suspense
      fallback={<TransitionOverlay open title="Loading ranking viewer…" subtitle="Preparing AI-trait results…" />}
    >
      <ImageRankingCodex
        // Remount when the task, bundle, or reviewer changes so the reducer / saver / edit-state never
        // carry across them (bundleIds aren't globally unique, and a newer bundle can land on the same
        // task in place, so the in-component state keys could otherwise collide or go stale).
        key={`${bundle.taskPath}:${bundle.bundleId}:${user?.uid ?? ""}`}
        orgBundle={{
          storagePath: bundle.bundleStoragePath,
          bundleId: bundle.bundleId,
          label: "AI traits",
          taskPath: bundle.taskPath,
          orgId: bundle.orgId,
        }}
        canEdit={canEdit}
        reviewerUid={user?.uid}
      />
    </Suspense>
  );
}

function AiTraitsNotice({ backTo, title, message }: { backTo: string; title: string; message: string }) {
  return (
    <div className="mx-auto max-w-md space-y-4 py-16 text-center">
      <h1 className="text-2xl font-semibold">{title}</h1>
      <p className="text-muted-foreground">{message}</p>
      <Link to={backTo} className="text-primary underline">
        ← Back to task
      </Link>
    </div>
  );
}
